import { Switch } from "@xola/ui-kit";
import { FormField, InputField, TextareaField } from "common";
import React, { useCallback, useMemo } from "react";
import { Experience } from "types";

interface PromotePrivatePricingSelectorProps {
    value: Experience;
    onChange: (key: string, value: any) => void;
}

const defaultEnabled = true;
const defaultHidePrivacyToggle = false;
const defaultTitle = "Upgrade to a Private Experience";
const defaultDescription =
    "Your experience may be shared with another party. If you prefer to attend with just your group, use this option to block off the remaining spots.";

export const PromotePrivatePricingSelector = ({ value, onChange }: PromotePrivatePricingSelectorProps) => {
    const upsellPreference = useMemo(
        () => ({
            enabled: value.upsellPreference?.enabled ?? defaultEnabled,
            hidePrivacyToggle: value.upsellPreference?.hidePrivacyToggle ?? defaultHidePrivacyToggle,
            title: value.upsellPreference?.title ?? defaultTitle,
            description: value.upsellPreference?.description ?? defaultDescription,
        }),
        [value.upsellPreference],
    );

    const handleToggleUpsell = useCallback(
        (isChecked: boolean) => {
            const updatedUpsellPreference = {
                ...upsellPreference,
                enabled: isChecked,
                hidePrivacyToggle: isChecked && upsellPreference.hidePrivacyToggle,
                title: isChecked ? upsellPreference.title : defaultTitle,
                description: isChecked ? upsellPreference.description : defaultDescription,
            };

            onChange("upsellPreference", updatedUpsellPreference);
        },
        [upsellPreference, onChange],
    );

    const handleToggleHidePrivacyToggle = useCallback(
        (isChecked: boolean) => {
            const updatedUpsellPreference = {
                ...upsellPreference,
                hidePrivacyToggle: isChecked,
            };

            onChange("upsellPreference", updatedUpsellPreference);
        },
        [upsellPreference, onChange],
    );

    const handleChange = useCallback(
        (key: string, value: any) => {
            const updatedUpsellPreference = { ...upsellPreference, [key]: value };
            onChange("upsellPreference", updatedUpsellPreference);
        },
        [upsellPreference, onChange],
    );

    return (
        <div className="space-y-6">
            <Switch.Group>
                <div className="flex flex-col space-y-2">
                    <div>
                        <span>
                            <Switch isChecked={upsellPreference.enabled} size="medium" onChange={handleToggleUpsell} />
                            <Switch.Label direction="right" className="ml-3">
                                Promote private pricing as an upsell
                            </Switch.Label>
                        </span>
                    </div>
                    {upsellPreference.enabled && (
                        <div>
                            <span>
                                <Switch
                                    isChecked={upsellPreference.hidePrivacyToggle}
                                    size="medium"
                                    onChange={handleToggleHidePrivacyToggle}
                                />
                                <Switch.Label direction="right" className="ml-3">
                                    Hide the public/private selector toggle (not recommended)
                                </Switch.Label>
                            </span>
                        </div>
                    )}
                </div>
            </Switch.Group>
            {upsellPreference.enabled && (
                <div className="space-y-4">
                    <FormField prevalidate name="upsellPreference.title" label="Banner Title">
                        <InputField
                            isRequired
                            value={upsellPreference.title}
                            onChange={(e) => handleChange("title", e.target.value)}
                        />
                    </FormField>
                    <FormField prevalidate name="upsellPreference.description" label="Motivational Content">
                        <TextareaField
                            isRequired
                            rows={4}
                            value={upsellPreference.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                        />
                    </FormField>
                </div>
            )}
        </div>
    );
};
